import { removeTrailingSlashes } from '@creator/ui/utils/url-utils';
import { Suspense, lazy, memo } from 'react';
import type { FC, PropsWithChildren } from 'react';
import { Route, Routes } from 'react-router';
import AuthRequiredRoute from './components/Auth/AuthRequiredRoute/AuthRequiredRoute';
import NetworkAdminRequiredRoute from './components/Auth/NetworkAdminRequiredRoute/NetworkAdminRequiredRoute';
import NoAuthRequiredRoute from './components/Auth/NoAuthRequiredRoute/NoAuthRequiredRoute';
import { getUrlMappingTokenRouteBasePaths } from './model/config/helpers';
import TokenPage from './pages/TokenPage/TokenPage';
// Lazy load all page components

const AdsDebugPage = lazy(() => import('./pages/AdsDebugPage/AdsDebugPage'));
const BbsDirectoryPage = lazy(() => import('./pages/BbsDirectoryPage/BbsDirectoryPage'));
const CreateAccountPage = lazy(() => import('./pages/CreateAccountPage/CreateAccountPage'));
const LoginPage = lazy(() => import('./pages/Login/Login'));
const SignInWithEmailLinkPage = lazy(() => import('./pages/SignInWithEmailLink/SignInWithEmailLink'));
const FiatInSuccessPage = lazy(() => import('./pages/FiatInSuccess/FiatInSuccess'));
const CreateTokenPage = lazy(() => import('./pages/CreateTokenPage/CreateTokenPage'));
const ChangePasswordWithEmailLinkPage = lazy(() => import('./pages/ChangePasswordWithEmailLink/ChangePasswordWithEmailLink'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage/NotificationsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage/NotFoundPage'));
const UserPageById = lazy(() => import('./pages/UserPage/UserPageById'));
const SearchPage = lazy(() => import('./pages/SearchPage/SearchPage'));
const AccountSettingsPage = lazy(() => import('./pages/AccountSettingsPage/AccountSettingsPage'));
const ForumPickerPage = lazy(() => import('./pages/ForumPickerPage/ForumPickerPage'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage/NotificationSettingsPage'));
const DirectMessagePage = lazy(() => import('./pages/DirectMessagePage/DirectMessagePage'));
const CreateExternalArticleCommentPage = lazy(() => import('./pages/CreateExternalArticleCommentPage/CreateExternalArticleCommentPage'));
const UserPage = lazy(() => import('./pages/UserPage/UserPage'));
const CreatePostPage = lazy(() => import('./pages/CreatePostPage/CreatePostPage'));
const CreateAnonymousPostPage = lazy(() => import('./pages/CreatePostPage/CreateAnonymousPostPage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage/AdminSettingsPage'));
const PostResponsePage = lazy(() => import('./pages/PostResponsePage/PostResponsePage'));
const PostResponseThreadPage = lazy(() => import('./pages/PostResponsePage/PostResponseThreadPage'));
const DomainMainMenuEditorPage = lazy(() => import('./pages/NetworkAdminDashboard/DomainMainMenuEditorPage/DomainMainMenuEditorPage'));
const NetworkAdminDashboard = lazy(() => import('./pages/NetworkAdminDashboard/NetworkAdminDashboard'));

const AppRoutes: FC<PropsWithChildren> = () => {
    function renderTokenRoutes() {
        const tokenRouteBasePaths = getUrlMappingTokenRouteBasePaths(); // e.g. ['/nhl', ''] empty string for root default token ('/')

        return tokenRouteBasePaths.map(tokenRouteBasePath => {
            // Use the utility function to clean the base path
            const cleanedBasePath = removeTrailingSlashes(tokenRouteBasePath);
            // Construct the final path
            const basePath = `${cleanedBasePath}/:tokenSlugOrName`;
            return (
                <Route
                    key={tokenRouteBasePath}
                    path={`${basePath}/*`}
                    element={
                        <Routes>
                            {/* Most specific routes first */}
                            <Route
                                path="admin-settings/:tab?"
                                element={
                                    <AuthRequiredRoute>
                                        <AdminSettingsPage />
                                    </AuthRequiredRoute>
                                }
                            />
                            <Route path="tab/:tab" element={<TokenPage />} />
                            <Route path="video/:videoIdentifier" element={<TokenPage />} />
                            <Route path="author/:slugOrDisplayName/message" element={<DirectMessagePage />} />
                            <Route path="author/:slugOrDisplayName/:tab?" element={<UserPage />} />

                            <Route path="community/:identifier" element={<TokenPage />} />
                            <Route path="community/:flairId/:identifier" element={<TokenPage />} />
                            <Route path="community/:identifier/comment/:commentId/:reply?/:replyId?" element={<TokenPage />} />
                            <Route path="community/:flairId/:identifier/comment/:commentId/:reply?/:replyId?" element={<TokenPage />} />

                            <Route path=":identifier" element={<TokenPage />} />
                            <Route path=":flairId/:identifier" element={<TokenPage />} />
                            <Route path=":identifier/comment/:commentId/:reply?/:replyId?" element={<TokenPage />} />
                            <Route path=":flairId/:identifier/comment/:commentId/:reply?/:replyId?" element={<TokenPage />} />

                            {/* General routes */}
                            <Route path="" element={<TokenPage />} />
                        </Routes>
                    }
                />
            );
        });
    }

    return (
        <Suspense fallback={null}>
            <Routes>
                <Route path="/" element={<TokenPage />} />
                <Route path="/bbs" element={<BbsDirectoryPage />} />
                <Route path="/bbs/thread/:threadEndResponseId/:responseId?" element={<PostResponseThreadPage />} />
                <Route path="/bbs/response/:responseId" element={<PostResponsePage />} />
                <Route
                    path="/bbs/create-token"
                    element={
                        <AuthRequiredRoute>
                            <CreateTokenPage />
                        </AuthRequiredRoute>
                    }
                />
                <Route path="/bbs/search/:type?" element={<SearchPage />} />
                <Route path="/bbs/:tokenSlugOrName/search/:type?" element={<SearchPage />} />
                <Route path="/bbs/404" element={<NotFoundPage />} />
                <Route path="/bbs/picker" element={<ForumPickerPage />} />
                <Route path="/bbs/email-sign-in" element={<SignInWithEmailLinkPage />} />
                <Route path="/bbs/email-change-password" element={<ChangePasswordWithEmailLinkPage />} />
                <Route
                    path="/bbs/my-account/settings"
                    element={
                        <AuthRequiredRoute>
                            <AccountSettingsPage />
                        </AuthRequiredRoute>
                    }
                />
                <Route path="/author/:id" element={<UserPageById />} />
                <Route path="/bbs/singup" element={<CreateAccountPage />} />
                <Route
                    path="/bbs/login"
                    element={
                        <NoAuthRequiredRoute>
                            <LoginPage />
                        </NoAuthRequiredRoute>
                    }
                />
                <Route path="/bbs/fiat-in-success" element={<FiatInSuccessPage />} />
                <Route
                    path="/bbs/notifications/:tab?"
                    element={
                        <AuthRequiredRoute>
                            <NotificationsPage />
                        </AuthRequiredRoute>
                    }
                />
                <Route
                    path="/bbs/my-profile/settings/notifications"
                    element={
                        <AuthRequiredRoute>
                            <NotificationSettingsPage />
                        </AuthRequiredRoute>
                    }
                />
                <Route
                    path="/bbs/:tokenSlugOrName/posts/create/:postId?"
                    element={
                        <AuthRequiredRoute>
                            <CreatePostPage />
                        </AuthRequiredRoute>
                    }
                />
                <Route path="/bbs/:tokenSlugOrName/posts/create/anonymous" element={<CreateAnonymousPostPage />} />
                <Route
                    path="/bbs/external-article-comment"
                    element={
                        <AuthRequiredRoute>
                            <CreateExternalArticleCommentPage />
                        </AuthRequiredRoute>
                    }
                />
                <Route
                    path="/bbs/network-admin-dashboard"
                    element={
                        <NetworkAdminRequiredRoute>
                            <NetworkAdminDashboard />
                        </NetworkAdminRequiredRoute>
                    }
                />
                <Route
                    path="/bbs/network-admin-dashboard/domain-main-menu-editor"
                    element={
                        <NetworkAdminRequiredRoute>
                            <DomainMainMenuEditorPage />
                        </NetworkAdminRequiredRoute>
                    }
                />
                <Route path="/bbs/ad-debug-page" element={<AdsDebugPage />} />
                {renderTokenRoutes()}

                {/* Fallback route */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </Suspense>
    );
};

export default memo(AppRoutes);
